import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import React, { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "../../context/UserContext";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

// Interface for bulk translation items
interface BulkTranslationItem {
    id: number;
    en_content: string;
    hu_content?: string;
    status?: string;
    file_name?: string;
}

const TömegesFordító = () => {
    const { gameId, versionId } = useParams();
    const { toast } = useToast();
    const { user } = useUser();

    const [selectedFileFilter, setSelectedFileFilter] = useState<string | null>(null);
    const [translations, setTranslations] = useState<Map<number, string>>(new Map()); // Track translations by ID
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useState<"bulk" | "search">("bulk");
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState<"content" | "name">("content");
    const [activeSearchType, setActiveSearchType] = useState<"content" | "name">("content");
    const [hideTranslated, setHideTranslated] = useState(false);
    const [caseSensitive, setCaseSensitive] = useState(true);
    const [searchTranslations, setSearchTranslations] = useState<Map<number, string>>(new Map());
    const [emptyTranslationsPerItem, setEmptyTranslationsPerItem] = useState<Set<number>>(new Set());
    const [emptySearchTranslationsPerItem, setEmptySearchTranslationsPerItem] = useState<Set<number>>(new Set());
    const [enHeights, setEnHeights] = useState<Map<number, number>>(new Map()); // Track English text heights by ID
    const enRefs = React.useRef<Map<number, HTMLDivElement | null>>(new Map());

    // Dictionary search panel state
    const [dictionarySearchTerm, setDictionarySearchTerm] = useState("");
    const [dictionarySearchQuery, setDictionarySearchQuery] = useState("");
    const [dictionarySearchType, setDictionarySearchType] = useState<"content" | "name">("content");
    const [dictionaryActiveSearchType, setDictionaryActiveSearchType] = useState<"content" | "name">("content");
    const [dictionaryHideTranslated, setDictionaryHideTranslated] = useState(false);
    const [dictionaryCaseSensitive, setDictionaryCaseSensitive] = useState(true);

    // Fetch all version files with pending translations
    const { data: versionFilesWithPending } = useQuery({
        queryKey: ["versionFilesWithPending", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/bulk_translations/files_with_pending`);
            if (!response.ok) throw new Error("Hálózati válasz nem megfelelő");
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });

    // Set the first file ID if no file is selected
    useEffect(() => {
        if (!selectedFileFilter && versionFilesWithPending?.files && versionFilesWithPending.files.length > 0) {
            const firstFileId = versionFilesWithPending.files[0].id.toString();
            setSelectedFileFilter(firstFileId);
        }
    }, [versionFilesWithPending, selectedFileFilter]);

    // Fetch bulk translation data (20 rows)
    const { data: bulkTranslationData, isLoading: isLoadingBulk, refetch } = useQuery({
        queryKey: ["bulkTranslations", gameId, versionId, selectedFileFilter, currentPage],
        queryFn: async () => {
            if (!selectedFileFilter) return { items: [], total_items: 0, total_pages: 0, current_page: 1 };
            const response = await fetch(
                `/api/games/${gameId}/versions/${versionId}/bulk_translations?file_id=${selectedFileFilter}&limit=20&page=${currentPage}`
            );
            if (!response.ok) throw new Error("Adatok lekérése sikertelen");
            return response.json();
        },
        enabled: !!gameId && !!versionId && !!selectedFileFilter,
    });

    // Fetch search results
    const { data: searchData, isLoading: isLoadingSearch, isError: isSearchError, error: searchError, refetch: refetchSearch } = useQuery({
        queryKey: ['bulkTranslationsSearch', gameId, searchQuery, activeSearchType, hideTranslated, caseSensitive],
        queryFn: async () => {
            if (!searchQuery) return { results: [], count: 0 };
            const response = await fetch(`/api/games/${gameId}/lista_search?q=${encodeURIComponent(searchQuery)}&type=${activeSearchType}&hide_translated=${hideTranslated}&case_sensitive=${caseSensitive}&hide_suggested=true&include_file_id=true`);
            if (!response.ok) throw new Error('Keresés sikertelen');
            return response.json();
        },
        enabled: !!gameId && !!searchQuery && viewMode === "search",
    });

    // Fetch dictionary entries
    const { data: dictionaryEntries, isLoading: isLoadingDictionary } = useQuery({
        queryKey: ["dictionaryEntries"],
        queryFn: async () => {
            const response = await fetch("/api/dictionary_entries");
            if (!response.ok) throw new Error("Szótár adatok lekérése sikertelen");
            return response.json();
        },
    });

    // Fetch dictionary search results (left panel)
    const { data: dictionarySearchData, isLoading: isLoadingDictionarySearch, isError: isDictionarySearchError, error: dictionarySearchError } = useQuery({
        queryKey: ['dictionarySearchGomb10', gameId, dictionarySearchQuery, dictionaryActiveSearchType, dictionaryHideTranslated, dictionaryCaseSensitive],
        queryFn: async () => {
            if (!dictionarySearchQuery) return { results: [], count: 0 };
            const response = await fetch(`/api/games/${gameId}/lista_search?q=${encodeURIComponent(dictionarySearchQuery)}&type=${dictionaryActiveSearchType}&hide_translated=${dictionaryHideTranslated}&case_sensitive=${dictionaryCaseSensitive}`);
            if (!response.ok) throw new Error('Keresés sikertelen');
            return response.json();
        },
        enabled: !!gameId && !!dictionarySearchQuery,
    });

    // Measure English text heights
    useEffect(() => {
        const newHeights = new Map<number, number>();
        enRefs.current.forEach((el, id) => {
            if (el) {
                const height = el.offsetHeight;
                newHeights.set(id, height);
            }
        });
        setEnHeights(newHeights);
    }, [bulkTranslationData?.items, searchData?.results]);

    // Mutation for saving translations - individual translations
    const saveSingleTranslationMutation = useMutation({
        mutationFn: async ({ extractedTextId, translatedText, uploadedZipId, userId }: { extractedTextId: number; translatedText: string; uploadedZipId: string; userId: string }) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");

            // Ha admin vagy translater_admin, automatikusan elfogadott legyen
            const isAutoAccept = !!(user?.admin || user?.translater_admin);

            const response = await fetch(`/api/extracted_texts/${extractedTextId}/translate`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    uploaded_zip_id: uploadedZipId,
                    translation: {
                        text_content: translatedText,
                        language: "HU",
                        user_id: userId,
                        auto_accept: isAutoAccept === true ? true : false,
                    }
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Mentés sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({
                title: "Sikeres mentés!",
                description: "Fordítás mentve és Discord értesítés elküldve.",
            });
            setTranslations(new Map());
            refetch();
        },
        onError: (error) => {
            toast({
                title: "Hiba",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Mutation for saving all translations at once
    const saveBulkTranslationsMutation = useMutation({
        mutationFn: async () => {
            const userId = getUserIdFromMeta();
            if (!userId) throw new Error("Felhasználó ID nem található");

            const uploadedZips = await fetch(
                `/api/games/${gameId}/versions/${versionId}/translation_files/${selectedFileFilter}/uploaded_zips?include_completed=true`
            ).then(r => r.json());

            const enZip = uploadedZips.find((z: any) => z.language === 'EN');
            if (!enZip) throw new Error("EN ZIP nem található");

            const translationPromises = Array.from(translations.entries()).map(([id, hu_content]) => {
                return saveSingleTranslationMutation.mutateAsync({
                    extractedTextId: id,
                    translatedText: hu_content,
                    uploadedZipId: enZip.id.toString(),
                    userId,
                });
            });

            return Promise.all(translationPromises);
        },
        onSuccess: () => {
            toast({
                title: "Sikeres tömeges mentés!",
                description: `${translations.size} fordítás mentve.`,
            });
            setTranslations(new Map());
            refetch();
        },
        onError: (error) => {
            toast({
                title: "Hiba",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Mutation for saving search result translations
    const saveSearchTranslationMutation = useMutation({
        mutationFn: async ({ extractedTextId, translatedText }: { extractedTextId: number; translatedText: string }) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const userId = getUserIdFromMeta();
            if (!userId) throw new Error("Felhasználó ID nem található");

            // Get the uploaded zip ID - we need to find the translation file first
            // Try to get from the currently selected file filter, or default to finding any file
            let uploadedZips;

            if (selectedFileFilter) {
                // Use the selected file
                try {
                    const response = await fetch(
                        `/api/games/${gameId}/versions/${versionId}/translation_files/${selectedFileFilter}/uploaded_zips?include_completed=true`
                    );
                    if (response.ok) {
                        uploadedZips = await response.json();
                    }
                } catch (e) {
                    // Fallback if this fails
                    uploadedZips = null;
                }
            }

            // If we couldn't get zips from selected file, try getting from files_with_pending
            if (!uploadedZips || uploadedZips.length === 0) {
                const filesResponse = await fetch(
                    `/api/games/${gameId}/versions/${versionId}/bulk_translations/files_with_pending`
                );
                if (!filesResponse.ok) throw new Error("Fájlok lekérése sikertelen");

                const filesData = await filesResponse.json();
                if (!filesData.files || filesData.files.length === 0) {
                    throw new Error("Nincs elérhető fordítási fájl");
                }

                const firstFile = filesData.files[0];
                const zipsResponse = await fetch(
                    `/api/games/${gameId}/versions/${versionId}/translation_files/${firstFile.id}/uploaded_zips?include_completed=true`
                );
                if (!zipsResponse.ok) throw new Error("Uploaded zips lekérése sikertelen");
                uploadedZips = await zipsResponse.json();
            }

            const enZip = uploadedZips.find((z: any) => z.language === 'EN');
            if (!enZip) throw new Error("EN ZIP nem található");

            // Ha admin vagy translater_admin, automatikusan elfogadott legyen
            const isAutoAccept = !!(user?.admin || user?.translater_admin);

            const response = await fetch(`/api/extracted_texts/${extractedTextId}/translate`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    uploaded_zip_id: enZip.id.toString(),
                    translation: {
                        text_content: translatedText,
                        language: "HU",
                        user_id: userId,
                        auto_accept: isAutoAccept === true ? true : false,
                    }
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Mentés sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({
                title: "Sikeres mentés!",
                description: "Fordítás mentve.",
            });
            setSearchTranslations(new Map());
            refetchSearch();
        },
        onError: (error) => {
            toast({
                title: "Hiba",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    // Helper to get user ID from meta
    const getUserIdFromMeta = () => {
        const userIdTag = document.querySelector("meta[name='current-user-id']");
        return userIdTag?.getAttribute("content") || null;
    };

    // Helper to set English text ref
    const setEnRef = (id: number, el: HTMLDivElement | null) => {
        if (el) {
            enRefs.current.set(id, el);
        } else {
            enRefs.current.delete(id);
        }
    };

    // Get the height for a specific item
    const getEnHeight = (id: number): number => {
        return enHeights.get(id) || 100; // Default 100px if height not yet measured
    };

    const handleTranslationChange = (id: number, value: string) => {
        const newTranslations = new Map(translations);
        newTranslations.set(id, value);
        setTranslations(newTranslations);
    };

    const handleFileSelect = (fileId: string | null) => {
        setSelectedFileFilter(fileId);
        setCurrentPage(1); // Reset to first page
        setTranslations(new Map()); // Clear translations when switching file
        setEmptyTranslationsPerItem(new Set()); // Clear empty translations flag
    };

    const handleSaveSingleBulkTranslation = (itemId: number) => {
        const translatedText = translations.get(itemId);
        const isEmpty = translatedText === "" || !translatedText || translatedText.trim() === "";
        const isEmptyAccepted = emptyTranslationsPerItem.has(itemId);

        // Üres fordítás esetén ha nincs jóváhagyva, ne küldj el
        if (isEmpty && !isEmptyAccepted) {
            toast({
                title: "Üres fordítás",
                description: "Kérjük, hagyja jóvá az üres fordítást az checkbox-szal.",
                variant: "destructive",
            });
            return;
        }

        // Save just this one translation
        const userId = getUserIdFromMeta();
        if (!userId) {
            toast({
                title: "Hiba",
                description: "Felhasználó ID nem található",
                variant: "destructive",
            });
            return;
        }

        saveSingleTranslationMutation.mutate({
            extractedTextId: itemId,
            translatedText: translatedText || "",
            uploadedZipId: "",
            userId,
        });
    };

    const handleSaveAll = () => {
        if (translations.size === 0) {
            toast({
                title: "Nincs mit menteni",
                description: "Kérjük, írjon be fordításokat előbb.",
                variant: "destructive",
            });
            return;
        }
        saveBulkTranslationsMutation.mutate();
    };

    const handleSaveAllEmptyTranslations = () => {
        if (emptyTranslationsPerItem.size === 0) {
            toast({
                title: "Nincs kijelölt üres fordítás",
                description: "Kérjük, jelöljön ki legalább egy üres fordítást.",
                variant: "destructive",
            });
            return;
        }

        const userId = getUserIdFromMeta();
        if (!userId) {
            toast({
                title: "Hiba",
                description: "Felhasználó ID nem található",
                variant: "destructive",
            });
            return;
        }

        // Gather all empty translations and send them
        const emptyTranslationPromises = Array.from(emptyTranslationsPerItem).map((id) => {
            return saveSingleTranslationMutation.mutateAsync({
                extractedTextId: id,
                translatedText: "",
                uploadedZipId: "",
                userId,
            });
        });

        Promise.all(emptyTranslationPromises)
            .then(() => {
                toast({
                    title: "Sikeres tömeges mentés!",
                    description: `${emptyTranslationsPerItem.size} üres fordítás mentve.`,
                });
                setEmptyTranslationsPerItem(new Set());
                refetch();
            })
            .catch((error) => {
                toast({
                    title: "Hiba",
                    description: error.message,
                    variant: "destructive",
                });
            });
    };

    const handleSelectAllEmpty = () => {
        if (!bulkTranslationData?.items || bulkTranslationData.items.length === 0) {
            toast({
                title: "Nincsenek fordítandó szövegek",
                description: "Az aktuális oldal üres.",
                variant: "destructive",
            });
            return;
        }

        // Kijelölünk minden üres fordítást az aktuális oldalon, ahol az EN is üres
        const emptyIds = new Set(emptyTranslationsPerItem);
        let addedCount = 0;

        bulkTranslationData.items.forEach((item: BulkTranslationItem) => {
            const translationValue = translations.get(item.id) ?? item.hu_content ?? "";
            const isEmpty = translationValue === "";
            const isEnEmpty = !item.en_content || item.en_content.trim() === "";

            if (isEmpty && isEnEmpty && !emptyIds.has(item.id)) {
                emptyIds.add(item.id);
                addedCount++;
            }
        });

        setEmptyTranslationsPerItem(emptyIds);

        if (addedCount === 0) {
            toast({
                title: "Nincs új üres fordítás",
                description: "Az összes üres fordítás már ki van jelölve az aktuális oldalon (ahol az EN is üres).",
            });
        } else {
            toast({
                title: "Kijelölés kész",
                description: `${addedCount} üres fordítás kijelölve az aktuális oldalon (üres EN-nel). Összesen: ${emptyIds.size} db.`,
            });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            setSearchQuery(searchTerm);
            setActiveSearchType(searchType);
        }
    };

    const handleSearch = () => {
        setSearchQuery(searchTerm);
        setActiveSearchType(searchType);
    };

    const handleDictionaryKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            setDictionarySearchQuery(dictionarySearchTerm);
            setDictionaryActiveSearchType(dictionarySearchType);
        }
    };

    const handleDictionarySearch = () => {
        setDictionarySearchQuery(dictionarySearchTerm);
        setDictionaryActiveSearchType(dictionarySearchType);
    };

    const getSortedBulkItems = () => {
        if (!bulkTranslationData?.items) return [];
        return bulkTranslationData.items;
    };

    const handleSearchTranslationChange = (id: number, value: string) => {
        const newTranslations = new Map(searchTranslations);
        newTranslations.set(id, value);
        setSearchTranslations(newTranslations);
    };

    const handleSaveSearchTranslation = (itemId: number) => {
        const translatedText = searchTranslations.get(itemId);
        const isEmpty = translatedText === "" || !translatedText || translatedText.trim() === "";
        const isEmptyAccepted = emptySearchTranslationsPerItem.has(itemId);

        // Üres fordítás esetén ha nincs jóváhagyva, ne küldj el
        if (isEmpty && !isEmptyAccepted) {
            toast({
                title: "Üres fordítás",
                description: "Kérjük, hagyja jóvá az üres fordítást az checkbox-szal.",
                variant: "destructive",
            });
            return;
        }

        const userId = getUserIdFromMeta();
        if (!userId) {
            toast({
                title: "Hiba",
                description: "Felhasználó ID nem található",
                variant: "destructive",
            });
            return;
        }

        saveSearchTranslationMutation.mutate({
            extractedTextId: itemId,
            translatedText: translatedText || "",
        });
    };

    // Helper function to find dictionary match in text
    const findDictionaryMatch = (text: string) => {
        if (!dictionaryEntries || !text) return null;

        // Normalize text for comparison
        const normalizedText = text.trim().toLowerCase();

        // Look for exact match first
        let match = dictionaryEntries.find(
            (entry: any) => entry.english.toLowerCase() === normalizedText
        );

        // If no exact match, look for partial match
        if (!match) {
            match = dictionaryEntries.find(
                (entry: any) => normalizedText.includes(entry.english.toLowerCase())
            );
        }

        return match;
    };

    // Helper function to highlight and show tooltip for matched words
    const renderTextWithDictionaryHighlight = (text: string) => {
        const match = findDictionaryMatch(text);

        if (match) {
            // Find the exact position of the matched word (case-insensitive)
            const matchedWord = match.english;
            const regex = new RegExp(`\\b${matchedWord}\\b`, 'gi');
            const parts = text.split(regex);
            const matches = text.match(regex);

            if (matches) {
                return (
                    <span>
                        {parts.map((part, idx) => (
                            <React.Fragment key={idx}>
                                <span>{part}</span>
                                {idx < matches.length && (
                                    <span
                                        style={{ color: '#000000' }}
                                        className="bg-yellow-200 dark:bg-yellow-700 px-1 py-0.5 rounded cursor-help"
                                        title={`📚 ${match.hungarian}${match.note ? `\n📝 ${match.note}` : ''}`}
                                    >
                                        {matches[idx]}
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </span>
                );
            }
        }

        return <span>{text}</span>;
    };

    return (
        <div className="w-full mx-auto py-6 px-4">
            <Breadcrumb className="mb-6">
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink href="/games">Játékok</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink href={`/games/${gameId}`}>{gameId}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink href={`/games/${gameId}/versions/${versionId}`}>{versionId}</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <span>TömegesFordító</span>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="mb-6">
                <VersionNavigation gameId={gameId} versionId={versionId} />
            </div>

            <div className="w-full flex gap-6">
                {/* Dictionary Search Panel - Left */}
                <div className="w-80 flex-shrink-0">
                    <Card className="sticky top-6 h-fit">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <span>🔍</span>
                                Szópár kereső
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Search Input */}
                            <div className="space-y-2">
                                <Input
                                    placeholder={dictionarySearchType === "content" ? "Keresés angolul..." : "Keresés fájlnévben..."}
                                    value={dictionarySearchTerm}
                                    onChange={(e) => setDictionarySearchTerm(e.target.value)}
                                    onKeyPress={handleDictionaryKeyPress}
                                    className="w-full"
                                />
                                <div className="flex items-center gap-2 text-xs">
                                    <label className="flex items-center gap-1 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="dictionarySearchType"
                                            checked={dictionarySearchType === "content"}
                                            onChange={() => setDictionarySearchType("content")}
                                            className="w-3 h-3"
                                        />
                                        <span>Szöveg</span>
                                    </label>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="dictionarySearchType"
                                            checked={dictionarySearchType === "name"}
                                            onChange={() => setDictionarySearchType("name")}
                                            className="w-3 h-3"
                                        />
                                        <span>Fájlnév</span>
                                    </label>
                                </div>
                                <Button onClick={handleDictionarySearch} className="w-full" size="sm">
                                    Keresés
                                </Button>
                            </div>

                            {/* Search Filters */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer text-xs">
                                    <Checkbox
                                        checked={dictionaryHideTranslated}
                                        onCheckedChange={(checked) => setDictionaryHideTranslated(checked === true)}
                                    />
                                    <span>Lefordítottak elrejtése</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-xs">
                                    <Checkbox
                                        checked={dictionaryCaseSensitive}
                                        onCheckedChange={(checked) => setDictionaryCaseSensitive(checked === true)}
                                    />
                                    <span>Kis/nagybetű érzékenység</span>
                                </label>
                            </div>

                            {/* Search Results */}
                            <div className="max-h-[calc(100vh-400px)] overflow-y-auto space-y-3">
                                {isLoadingDictionarySearch && <p className="text-sm text-center py-2">Keresés...</p>}
                                {isDictionarySearchError && <p className="text-sm text-center py-2 text-red-500">Hiba: {dictionarySearchError?.message}</p>}

                                {!dictionarySearchQuery && (
                                    <p className="text-sm text-muted-foreground text-center py-4">Írj be egy keresőkifejezést.</p>
                                )}

                                {dictionarySearchData && dictionarySearchData.count > 0 && (
                                    <div className="space-y-3">
                                        <div className="text-sm font-semibold text-muted-foreground">Találatok: {dictionarySearchData.count}</div>
                                        {dictionarySearchData.results.map((item: any, index: number) => {
                                            const hasAccepted = item.accepted_translation;
                                            const hasPending = item.pending_translations && item.pending_translations.length > 0;

                                            return (
                                                <div key={`${item.id}-${index}`} className="border rounded p-2 bg-muted/50 space-y-2">
                                                    {/* English Text */}
                                                    <div>
                                                        <div className="text-sm font-semibold text-muted-foreground mb-1">🇬🇧 Angol</div>
                                                        <div className="text-sm bg-background p-2 rounded break-words">
                                                            {item.content}
                                                        </div>
                                                    </div>

                                                    {/* Hungarian Translations */}
                                                    {hasAccepted || hasPending ? (
                                                        <div className="space-y-1">
                                                            <div className="text-sm font-semibold text-muted-foreground mb-1">🇭🇺 Magyar</div>

                                                            {/* Accepted Translation */}
                                                            {hasAccepted && (
                                                                <div className="text-sm bg-background p-2 rounded break-words border-l-2 border-green-500">
                                                                    <span className="text-green-600 font-bold">✓ </span>
                                                                    {item.accepted_translation.content}
                                                                </div>
                                                            )}

                                                            {/* Pending Translations */}
                                                            {hasPending && item.pending_translations.map((trans: any, idx: number) => (
                                                                <div key={`${trans.id}-${idx}`} className="text-sm bg-background p-2 rounded break-words border-l-2 border-yellow-500">
                                                                    <span className="text-yellow-600 font-bold">⏳ </span>
                                                                    {trans.content}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm text-muted-foreground italic">Nincs fordítás</div>
                                                    )}

                                                    {/* File Info */}
                                                    {(item.file_name || item.name) && (
                                                        <div className="text-sm text-muted-foreground truncate" title={item.file_name || item.name}>
                                                            📄 {item.file_name || item.name}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {dictionarySearchData && dictionarySearchData.count === 0 && dictionarySearchQuery && (
                                    <p className="text-xs text-muted-foreground text-center py-4">
                                        Nincs találat a(z) "{dictionarySearchQuery}" kifejezésre.
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Translation Panel - Middle (flex-1) */}
                <div className="flex-1 w-full">
                    <Card>
                        <CardHeader>
                            <CardTitle>TömegesFordító - Tömeges fordítások</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* View Mode Tabs */}
                            <div className="flex gap-2 border-b">
                                <Button
                                    variant={viewMode === "bulk" ? "default" : "ghost"}
                                    onClick={() => setViewMode("bulk")}
                                    className="rounded-b-none"
                                >
                                    📋 Tömeges fordítás
                                </Button>
                                <Button
                                    variant={viewMode === "search" ? "default" : "ghost"}
                                    onClick={() => setViewMode("search")}
                                    className="rounded-b-none"
                                >
                                    🔍 Keresés
                                </Button>
                            </div>

                            {viewMode === "bulk" && (
                                <>
                                    {/* File Filter Buttons */}
                                    <div className="space-y-2">
                                        <h3 className="font-semibold mb-3">Fájl kiválasztása</h3>
                                        {versionFilesWithPending?.files && versionFilesWithPending.files.length > 0 ? (
                                            <div className="flex flex-wrap gap-2">
                                                {versionFilesWithPending.files.map((file: any) => (
                                                    <Button
                                                        key={file.id}
                                                        onClick={() => handleFileSelect(file.id.toString())}
                                                        variant={selectedFileFilter === file.id.toString() ? "default" : "outline"}
                                                    >
                                                        {file.name}
                                                    </Button>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 text-muted-foreground">
                                                Nincsenek fordítandó szövegek ebben a verzióban.
                                            </div>
                                        )}
                                    </div>

                                    {/* Translations Table */}
                                    <div className="space-y-4">
                                        {isLoadingBulk ? (
                                            <div className="text-center py-8 text-muted-foreground">Betöltés...</div>
                                        ) : bulkTranslationData?.items?.length > 0 ? (
                                            <>
                                                {/* Pagination Top */}
                                                <div className="flex items-center justify-between mb-4 p-3 bg-muted rounded">
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(1)}
                                                            disabled={currentPage === 1}
                                                            title="Első oldal"
                                                        >
                                                            &lt;&lt;
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                                            disabled={currentPage === 1}
                                                            title="Előző oldal"
                                                        >
                                                            &lt;
                                                        </Button>
                                                        <span className="text-sm font-medium mx-2">
                                                            {currentPage} / {bulkTranslationData?.total_pages || 1}
                                                        </span>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(Math.min(bulkTranslationData?.total_pages || 1, currentPage + 1))}
                                                            disabled={currentPage === bulkTranslationData?.total_pages}
                                                            title="Következő oldal"
                                                        >
                                                            &gt;
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(bulkTranslationData?.total_pages || 1)}
                                                            disabled={currentPage === bulkTranslationData?.total_pages}
                                                            title="Utolsó oldal"
                                                        >
                                                            &gt;&gt;
                                                        </Button>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <label className="text-sm font-medium">Ugrás:</label>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={bulkTranslationData?.total_pages || 1}
                                                            value={currentPage}
                                                            onChange={(e) => {
                                                                const value = parseInt(e.target.value);
                                                                if (value >= 1 && value <= (bulkTranslationData?.total_pages || 1)) {
                                                                    setCurrentPage(value);
                                                                }
                                                            }}
                                                            className="w-16"
                                                        />
                                                        <span className="text-sm text-muted-foreground">/ {bulkTranslationData?.total_pages || 1}</span>
                                                    </div>
                                                </div>

                                                <div className="space-y-6">
                                                    {getSortedBulkItems().map((item: BulkTranslationItem) => {
                                                        const translationValue = translations.get(item.id) ?? item.hu_content ?? "";
                                                        const isEmptyForditas = translationValue === "";
                                                        const isEmptyAccepted = emptyTranslationsPerItem.has(item.id);
                                                        const isEnEmpty = !item.en_content || item.en_content.trim() === "";

                                                        return (
                                                            <div key={item.id} className="border rounded-lg p-4 bg-card">
                                                                {/* Angol szöveg */}
                                                                <div className="mb-4">
                                                                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">Angol szöveg</h3>
                                                                    <div className="space-y-2">
                                                                        <div
                                                                            ref={(el) => setEnRef(item.id, el)}
                                                                            className="break-words text-base p-3 bg-muted rounded min-h-20 flex items-center"
                                                                        >
                                                                            {renderTextWithDictionaryHighlight(item.en_content)}
                                                                        </div>
                                                                        <div className="text-sm font-mono text-muted-foreground">
                                                                            <div className="flex items-center gap-2">
                                                                                <span>📄 {item.file_name || "N/A"}</span>
                                                                                {item.file_name && selectedFileFilter && (
                                                                                    <Link
                                                                                        to={`/games/${gameId}/versions/${versionId}/translate?highlight_id=${item.id}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="h-5 w-5 p-0 inline-flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                                                                                        title="Fájl megnyitása új ablakban"
                                                                                    >
                                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                        </svg>
                                                                                    </Link>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Magyar fordítás */}
                                                                <div className="mb-4">
                                                                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">Magyar fordítás</h3>
                                                                    <div className="space-y-2">
                                                                        <Textarea
                                                                            value={translationValue}
                                                                            onChange={(e) => handleTranslationChange(item.id, e.target.value)}
                                                                            placeholder="Írja be a magyar fordítást..."
                                                                            className="text-base"
                                                                            style={{
                                                                                minHeight: `${getEnHeight(item.id)}px`,
                                                                                resize: 'vertical'
                                                                            }}
                                                                            rows={3}
                                                                        />
                                                                        {isEmptyForditas && isEnEmpty && (
                                                                            <label className="flex items-center gap-2 cursor-pointer text-sm text-amber-600">
                                                                                <Checkbox
                                                                                    checked={isEmptyAccepted}
                                                                                    onCheckedChange={(checked) => {
                                                                                        const newSet = new Set(emptyTranslationsPerItem);
                                                                                        if (checked) {
                                                                                            newSet.add(item.id);
                                                                                        } else {
                                                                                            newSet.delete(item.id);
                                                                                        }
                                                                                        setEmptyTranslationsPerItem(newSet);
                                                                                    }}
                                                                                />
                                                                                <span>Üres fordítás beküldése</span>
                                                                            </label>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Gombok */}
                                                                <div className="flex gap-2 justify-end">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleTranslationChange(item.id, item.en_content)}
                                                                        title="Angol szöveg másolása"
                                                                    >
                                                                        📋
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => handleSaveSingleBulkTranslation(item.id)}
                                                                        disabled={
                                                                            (isEmptyForditas && !isEmptyAccepted) ||
                                                                            saveSingleTranslationMutation.isPending
                                                                        }
                                                                    >
                                                                        ✓
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Pagination Bottom */}
                                                <div className="flex items-center justify-between p-3 bg-muted rounded">
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(1)}
                                                            disabled={currentPage === 1}
                                                            title="Első oldal"
                                                        >
                                                            &lt;&lt;
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                                            disabled={currentPage === 1}
                                                            title="Előző oldal"
                                                        >
                                                            &lt;
                                                        </Button>
                                                        <span className="text-sm font-medium mx-2">
                                                            {currentPage} / {bulkTranslationData?.total_pages || 1}
                                                        </span>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(Math.min(bulkTranslationData?.total_pages || 1, currentPage + 1))}
                                                            disabled={currentPage === bulkTranslationData?.total_pages}
                                                            title="Következő oldal"
                                                        >
                                                            &gt;
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setCurrentPage(bulkTranslationData?.total_pages || 1)}
                                                            disabled={currentPage === bulkTranslationData?.total_pages}
                                                            title="Utolsó oldal"
                                                        >
                                                            &gt;&gt;
                                                        </Button>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <label className="text-sm font-medium">Ugrás:</label>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={bulkTranslationData?.total_pages || 1}
                                                            value={currentPage}
                                                            onChange={(e) => {
                                                                const value = parseInt(e.target.value);
                                                                if (value >= 1 && value <= (bulkTranslationData?.total_pages || 1)) {
                                                                    setCurrentPage(value);
                                                                }
                                                            }}
                                                            className="w-16"
                                                        />
                                                        <span className="text-sm text-muted-foreground">/ {bulkTranslationData?.total_pages || 1}</span>
                                                    </div>
                                                </div>

                                                {/* Save All Button */}
                                                <div className="flex justify-end gap-2 pt-4">
                                                    <Button
                                                        onClick={handleSelectAllEmpty}
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        Üresek kijelölés
                                                    </Button>
                                                    {emptyTranslationsPerItem.size > 0 && (
                                                        <Button
                                                            onClick={handleSaveAllEmptyTranslations}
                                                            disabled={saveSingleTranslationMutation.isPending}
                                                            className="bg-amber-600 hover:bg-amber-700"
                                                        >
                                                            {saveSingleTranslationMutation.isPending
                                                                ? "Mentés folyamatban..."
                                                                : `Üresek beküldése (${emptyTranslationsPerItem.size})`}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        onClick={handleSaveAll}
                                                        disabled={
                                                            translations.size === 0 ||
                                                            saveBulkTranslationsMutation.isPending
                                                        }
                                                        className="bg-green-600 hover:bg-green-700"
                                                    >
                                                        {saveBulkTranslationsMutation.isPending
                                                            ? "Mentés folyamatban..."
                                                            : `Összes mentése (${translations.size})`}
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center py-8 text-muted-foreground">
                                                Nincsenek fordítandó szövegek ebben a fájlban.
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {viewMode === "search" && (
                                <div className="space-y-4">
                                    <p className="text-sm text-muted-foreground">Keress meg szövegeket angol vagy fájlnévben, majd közvetlenül itt fordítsd le őket.</p>

                                    {/* Search Bar */}
                                    <div className="flex gap-2 items-center">
                                        <Input
                                            placeholder={searchType === "content" ? "Keresés angol szövegben..." : "Keresés fájlnévben..."}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            className="flex-1"
                                        />
                                        <div className="flex items-center gap-3 text-sm">
                                            <label className="flex items-center gap-1 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="searchType"
                                                    checked={searchType === "content"}
                                                    onChange={() => setSearchType("content")}
                                                    className="w-4 h-4"
                                                />
                                                <span>Szövegben</span>
                                            </label>
                                            <label className="flex items-center gap-1 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="searchType"
                                                    checked={searchType === "name"}
                                                    onChange={() => setSearchType("name")}
                                                    className="w-4 h-4"
                                                />
                                                <span>Fájlnév</span>
                                            </label>
                                        </div>
                                        <Button onClick={handleSearch}>Keresés</Button>
                                    </div>

                                    {/* Search Filters */}
                                    <div className="flex items-center gap-4 flex-wrap">
                                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                                            <Checkbox
                                                checked={hideTranslated}
                                                onCheckedChange={(checked) => setHideTranslated(checked === true)}
                                            />
                                            <span>Lefordítottak elrejtése</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                                            <Checkbox
                                                checked={caseSensitive}
                                                onCheckedChange={(checked) => setCaseSensitive(checked === true)}
                                            />
                                            <span>Kis/nagybetű érzékenység</span>
                                        </label>
                                    </div>

                                    {/* Sort Options */}
                                    {searchData && searchData.count > 0 && (
                                        <div className="text-sm bg-muted p-2 rounded text-muted-foreground">
                                            Találatok rendezve szöveg hossza szerint
                                        </div>
                                    )}

                                    {/* Search Results */}
                                    {isLoadingSearch && <p className="text-center py-4">Keresés...</p>}
                                    {isSearchError && <p className="text-center py-4 text-red-500">Hiba: {searchError?.message}</p>}

                                    {!searchQuery && (
                                        <p className="text-muted-foreground text-sm text-center py-4">Írd be a keresőkifejezést és nyomj Entert vagy kattints a Keresés gombra.</p>
                                    )}

                                    {searchData && searchData.count > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-4">Találat: {searchData.count} szöveg</h3>
                                            <div className="overflow-x-auto">
                                                <div className="space-y-6">
                                                    {searchData.results.map((item: any) => {
                                                        const translationValue = searchTranslations.get(item.id) ?? item.hu_content ?? "";
                                                        const isEmptyForditas = translationValue === "";
                                                        const isEmptyAccepted = emptySearchTranslationsPerItem.has(item.id);
                                                        const isEnEmpty = !item.content || item.content.trim() === "";

                                                        return (
                                                            <div key={item.id} className="border rounded-lg p-4 bg-card">
                                                                {/* Angol szöveg */}
                                                                <div className="mb-4">
                                                                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">Angol szöveg</h3>
                                                                    <div className="space-y-2">
                                                                        <div
                                                                            ref={(el) => setEnRef(item.id, el)}
                                                                            className="break-words text-base p-3 bg-muted rounded min-h-20 flex items-center"
                                                                        >
                                                                            {renderTextWithDictionaryHighlight(item.content)}
                                                                        </div>
                                                                        <div className="text-sm font-mono text-muted-foreground">
                                                                            <div className="flex items-center gap-2">
                                                                                <span>📄 {item.file_name || item.name || "N/A"}</span>
                                                                                {(item.file_name || item.name) && item.id && (
                                                                                    <Link
                                                                                        to={`/games/${gameId}/versions/${versionId}/translate?highlight_id=${item.id}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="h-5 w-5 p-0 inline-flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 rounded flex-shrink-0"
                                                                                        title="Fájl megnyitása új ablakban"
                                                                                    >
                                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                        </svg>
                                                                                    </Link>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Magyar fordítás */}
                                                                <div className="mb-4">
                                                                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">Magyar fordítás</h3>
                                                                    <div className="space-y-2">
                                                                        <Textarea
                                                                            value={translationValue}
                                                                            onChange={(e) => handleSearchTranslationChange(item.id, e.target.value)}
                                                                            placeholder="Írja be a magyar fordítást..."
                                                                            className="text-base"
                                                                            style={{
                                                                                minHeight: `${getEnHeight(item.id)}px`,
                                                                                resize: 'vertical'
                                                                            }}
                                                                            rows={3}
                                                                        />
                                                                        {isEmptyForditas && isEnEmpty && (
                                                                            <label className="flex items-center gap-2 cursor-pointer text-sm text-amber-600">
                                                                                <Checkbox
                                                                                    checked={isEmptyAccepted}
                                                                                    onCheckedChange={(checked) => {
                                                                                        const newSet = new Set(emptySearchTranslationsPerItem);
                                                                                        if (checked) {
                                                                                            newSet.add(item.id);
                                                                                        } else {
                                                                                            newSet.delete(item.id);
                                                                                        }
                                                                                        setEmptySearchTranslationsPerItem(newSet);
                                                                                    }}
                                                                                />
                                                                                <span>Üres fordítás beküldése</span>
                                                                            </label>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Gombok */}
                                                                <div className="flex gap-2 justify-end">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => handleSearchTranslationChange(item.id, item.content)}
                                                                        title="Angol szöveg másolása"
                                                                    >
                                                                        📋
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            handleSaveSearchTranslation(item.id);
                                                                        }}
                                                                        disabled={
                                                                            (isEmptyForditas && !isEmptyAccepted) ||
                                                                            saveSearchTranslationMutation.isPending
                                                                        }
                                                                    >
                                                                        ✓
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {searchData && searchData.count === 0 && searchQuery && (
                                        <div className="text-center py-8 text-muted-foreground">
                                            Nincs találat a(z) "{searchQuery}" kifejezésre.
                                        </div>
                                    )}
                                </div>
                            )}

                        </CardContent>
                    </Card>
                </div>

                {/* Dictionary Panel - Right (fixed width) */}
                <div className="w-80 flex-shrink-0">
                    <Card className="sticky top-6 h-fit">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <span>📚</span>
                                Szótár ({dictionaryEntries?.length || 0})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {isLoadingDictionary ? (
                                <div className="text-sm text-muted-foreground text-center py-4">Szótár betöltése...</div>
                            ) : dictionaryEntries && dictionaryEntries.length > 0 ? (
                                <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
                                    {dictionaryEntries.map((entry: any) => (
                                        <div key={entry.id} className="p-3 bg-muted rounded-lg border hover:bg-muted/80 transition-colors">
                                            <div className="font-semibold text-base text-foreground mb-1">{entry.english}</div>
                                            <div className="text-base text-blue-600 dark:text-blue-400 mb-2">{entry.hungarian}</div>
                                            {entry.note && (
                                                <div className="text-sm text-muted-foreground italic border-t pt-2 mt-2">
                                                    📝 {entry.note}
                                                </div>
                                            )}
                                            {entry.user && (
                                                <div className="text-sm text-muted-foreground mt-1">
                                                    <span className="opacity-70">by {entry.user.username}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-muted-foreground text-center py-4">
                                    <p className="mb-2">Nincsenek szótárbejegyzések.</p>
                                    <p className="text-xs">
                                        <Link to={`/games/${gameId}/versions/${versionId}/gomb6`} className="text-blue-600 hover:underline">
                                            Hozzáadás a szótárhoz
                                        </Link>
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default TömegesFordító;
